import React, { useEffect, useState, useCallback, useMemo } from "react";
import { SlArrowRight } from "react-icons/sl";
import { ScheduleSideFormContainer, SideFormList } from "../styled/calendarEntry";
import SideFormItem from "./sideFormItem";
import { useSelector } from "react-redux";

// 상수 정의
const MENU_TYPES = {
  PROJECT: "project",
  MANAGER: "manager",
  WORKER: "worker",
};

const SideForm = ({
  formData,
  setFormData,
  affilEmp,
  actSelType,
  saveToLocalStorage,
  toggleSideForm,
  projectMberInfo,
  setActSelType,
  isSideMenuType,
  projectList,
  clickProject,
  isActive = true,
}) => {
  const [text, setText] = useState("");
  const [users, setUsers] = useState([]);
  const isDarkMode = useSelector((state) => state?.scheduler?.isDarkMode);
  const [allChecked, setAllChecked] = useState(false);
  const [projectMbers, setProjectMbers] = useState([]);
  const [projects, setProjects] = useState({});

  // 프로젝트 목록 초기화
  useEffect(() => {
    setProjects(projectList || {});
  }, [projectList]);

  // 사용자 목록 정렬 및 필터링
  const sortedUsers = useMemo(() => {
    if (!affilEmp || affilEmp.length === 0) return [];
    return [...affilEmp].sort((a, b) => a.mberNo - b.mberNo);
  }, [affilEmp]);

  useEffect(() => {
    setUsers(sortedUsers);
  }, [sortedUsers]);

  // 전체 선택 상태 계산
  const calculateAllChecked = useCallback(() => {
    if (actSelType) return false;

    // 일반 사용자들
    const availableUsers = users.filter((item) => item.mberNo !== formData.managerNo);

    // 프로젝트 멤버들
    const availableProjectMembers = projectMbers
      .filter((org) => parseInt(org.orgNo) !== parseInt(formData.orgNo))
      .flatMap((org) => org.mbers);

    // 모든 선택 가능한 사용자들
    const allAvailableUsers = [...availableUsers, ...availableProjectMembers];

    return (
      allAvailableUsers.length > 0 &&
      allAvailableUsers.every((item) => formData.workerList?.[item.mberNo])
    );
  }, [users, projectMbers, formData.managerNo, formData.orgNo, formData.workerList, actSelType]);

  useEffect(() => {
    setAllChecked(calculateAllChecked());
  }, [calculateAllChecked]);

  // 프로젝트 멤버 초기화
  useEffect(() => {
    setProjectMbers([...projectMberInfo]);
  }, [projectMberInfo]);

  // 실무자 제거 유틸리티 함수
  const removeWorkerFromList = useCallback((workerList, memberNo) => {
    const newWorkerList = { ...workerList };
    delete newWorkerList[memberNo];
    return newWorkerList;
  }, []);

  // 담당자 설정
  const setManager = useCallback(
    (item) => {
      if (!item || !item.mberNo || !item.mberNm) {
        console.error("유효하지 않은 사용자 정보입니다:", item);
        return;
      }

      setFormData((data) => {
        const updated = {
          ...data,
          managerNo: item.mberNo,
          managerNm: item.mberNm,
          workerList: removeWorkerFromList(data.workerList, item.mberNo),
        };

        try {
          saveToLocalStorage(updated);
        } catch (error) {
          console.error("로컬 스토리지 저장 중 오류가 발생했습니다:", error);
        }
        return updated;
      });
      toggleSideForm(true);
      setActSelType(false);
    },
    [setFormData, saveToLocalStorage, toggleSideForm, setActSelType, removeWorkerFromList],
  );

  // 실무자 선택 토글
  const toggleWorker = useCallback(
    (item) => {
      if (!item || !item.mberNo || !item.mberNm) {
        console.error("유효하지 않은 사용자 정보입니다:", item);
        return;
      }

      setFormData((data) => {
        const newWorkerList = { ...data.workerList };

        if (newWorkerList[item.mberNo]) {
          delete newWorkerList[item.mberNo];
        } else {
          newWorkerList[item.mberNo] = item.mberNm;
        }

        const updated = { ...data, workerList: newWorkerList };

        try {
          saveToLocalStorage(updated);
        } catch (error) {
          console.error("로컬 스토리지 저장 중 오류가 발생했습니다:", error);
        }
        return updated;
      });
    },
    [setFormData, saveToLocalStorage],
  );

  // 사용자 검색 필터
  const filterUsers = useCallback((userList, searchText) => {
    if (!searchText) return userList;

    return userList.filter(
      (user) =>
        (user.mberNm && user.mberNm.includes(searchText)) ||
        (user.tel && user.tel.includes(searchText)),
    );
  }, []);

  // 프로젝트 멤버 검색 필터
  const filterProjectMembers = useCallback((memberList, searchText, currentOrgNo) => {
    if (!searchText) return memberList;

    return memberList.filter(
      (org) =>
        org.orgNo !== currentOrgNo &&
        org.mbers.some(
          (user) =>
            (user.mberNm && user.mberNm.includes(searchText)) ||
            (user.tel && user.moblphonNo.includes(searchText)),
        ),
    );
  }, []);

  // 프로젝트 검색 필터
  const filterProjects = useCallback((projectList, searchText) => {
    if (!searchText) return projectList;

    return Object.fromEntries(
      Object.entries(projectList).filter(([label]) => label.includes(searchText)),
    );
  }, []);

  // 검색 처리
  const handleSearch = useCallback(
    (e) => {
      e.preventDefault();

      if (!text.trim()) return;

      try {
        if (isSideMenuType === MENU_TYPES.PROJECT) {
          setProjects(filterProjects(projects, text));
        } else {
          setUsers(filterUsers(affilEmp, text));
          setProjectMbers(filterProjectMembers(projectMberInfo, text, formData.orgNo));
        }
      } catch (error) {
        console.error("검색 중 오류가 발생했습니다:", error);
      }

      setText("");
    },
    [
      text,
      isSideMenuType,
      projects,
      affilEmp,
      projectMberInfo,
      formData.orgNo,
      filterUsers,
      filterProjectMembers,
      filterProjects,
    ],
  );

  // 전체 선택/해제
  const handleAllCheck = useCallback(
    (checked) => {
      setAllChecked(checked);

      if (actSelType) return;

      if (checked) {
        // 일반 사용자들 추가
        const userWorkers = users
          .filter((item) => item.mberNo !== formData.managerNo)
          .reduce((acc, curr) => ({ ...acc, [curr.mberNo]: curr.mberNm }), {});

        // 프로젝트 멤버들 추가
        const projectWorkers = projectMbers
          .filter((org) => parseInt(org.orgNo) !== parseInt(formData.orgNo))
          .flatMap((org) => org.mbers)
          .reduce((acc, curr) => ({ ...acc, [curr.mberNo]: curr.mberNm }), {});

        const newWorkerList = { ...userWorkers, ...projectWorkers };

        setFormData((data) => {
          const updated = { ...data, workerList: newWorkerList };

          try {
            saveToLocalStorage(updated);
          } catch (error) {
            console.error("로컬 스토리지 저장 중 오류가 발생했습니다:", error);
          }
          return updated;
        });
      } else {
        setFormData((data) => {
          const updated = { ...data, workerList: {} };

          try {
            saveToLocalStorage(updated);
          } catch (error) {
            console.error("로컬 스토리지 저장 중 오류가 발생했습니다:", error);
          }
          return updated;
        });
      }
    },
    [
      actSelType,
      users,
      projectMbers,
      formData.managerNo,
      formData.orgNo,
      setFormData,
      saveToLocalStorage,
    ],
  );

  return (
    <ScheduleSideFormContainer
      className={`font_16 scheduleSide ${isDarkMode} ${isActive ? "active" : ""}`}>
      <>
        <div className="sideFormHeader" onClick={() => toggleSideForm(false)}>
          <SlArrowRight />
          <span className="font_24">
            {isSideMenuType === MENU_TYPES.PROJECT ? "프로젝트" : actSelType ? "담당자" : "실무자"}{" "}
            선택
          </span>
        </div>
        <form onSubmit={(e) => handleSearch(e)}>
          <input
            type="text"
            placeholder={`${
              isSideMenuType === MENU_TYPES.PROJECT ? "프로젝트명을" : "이름 또는 전화번호를"
            } 입력해주세요.`}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <button type="submit">검색</button>
        </form>
        <div
          className={`section ${
            formData.gbn === "C" && isSideMenuType !== MENU_TYPES.PROJECT ? "cmpny" : "grp"
          }`}>
          {isSideMenuType === MENU_TYPES.PROJECT ? (
            <ul className="searchContent">
              <li className={`tit${Object.keys(projects).length > 0 ? "" : " not"}`}>
                {Object.keys(projects).length > 0 && <div></div>}
                <div>프로젝트명</div>
              </li>
              {Object.keys(projects).length > 0 ? (
                Object.entries(projects).map(([label, value]) => (
                  <SideFormList key={value}>
                    <label>
                      <div>
                        <input
                          type="radio"
                          name="project"
                          checked={formData?.projectNo === value}
                          onChange={() => clickProject({ key: label, value })}
                        />
                      </div>
                      <div>{label}</div>
                    </label>
                  </SideFormList>
                ))
              ) : (
                <SideFormList className="not">프로젝트가 존재하지 않습니다.</SideFormList>
              )}
            </ul>
          ) : (
            <>
              <ul className="searchContent">
                <li className={`tit${users.length > 0 ? "" : " not"}`}>
                  <div>
                    {!actSelType && (
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={(e) => handleAllCheck(e.target.checked)}
                      />
                    )}
                  </div>
                  <div>회원명</div>
                  {formData.gbn === "C" && <div>전화번호</div>}
                </li>
                {(() => {
                  const filteredUsers = users.filter(
                    (item) => actSelType || item.mberNo !== formData.managerNo,
                  );

                  return filteredUsers.length > 0 ? (
                    filteredUsers.map((item) => (
                      <SideFormItem
                        key={item.mberNo}
                        item={item}
                        formData={formData}
                        onToggle={actSelType ? setManager : toggleWorker}
                        actSelType={actSelType}
                      />
                    ))
                  ) : (
                    <SideFormList className="not">선택 가능한 인원이 없습니다.</SideFormList>
                  );
                })()}
              </ul>
              {projectMbers.length > 0 && formData.gbn === "C" && !isNaN(formData.projectNo) && (
                <div className="participants">
                  {projectMbers
                    .filter((org) => parseInt(org.orgNo) !== parseInt(formData.orgNo))
                    .map((org) => (
                      <>
                        <div>{org.orgNm ? org.orgNm : "회사명 미설정"}</div>
                        <ul>
                          {org.mbers.map((item, i) => (
                            <SideFormItem
                              key={item.mberNo}
                              item={item}
                              formData={formData}
                              onToggle={
                                actSelType
                                  ? // () => toast.error("외부업체는 담당자 설정이 불가능합니다.")
                                    setManager
                                  : toggleWorker
                              }
                              actSelType={actSelType}
                            />
                          ))}
                        </ul>
                      </>
                    ))}
                </div>
              )}
            </>
          )}
        </div>
        <div className="btnArea">
          <button onClick={() => toggleSideForm(false)}>확인</button>
        </div>
      </>
    </ScheduleSideFormContainer>
  );
};

export default SideForm;
